import type { ReactNode } from 'react';
import { Navigate } from 'react-router';

import { paths } from '@/app/routes/paths';
import { userHasPermission, useAuthStore } from '@/store';

export interface RequirePermissionProps {
  children: ReactNode;
  /** Permission key from `SessionUser.permissionKeys` (e.g. `participants.manage`). */
  permission: string;
  redirectTo?: string;
}

/**
 * Restricts a route to users whose session includes a permission key from the API.
 *
 * @example
 * <RequirePermission permission="participants.manage" redirectTo={paths.guardianDashboard}>
 *   <PatientsHubPage />
 * </RequirePermission>
 */
export function RequirePermission({
  children,
  permission,
  redirectTo = paths.dashboard,
}: RequirePermissionProps) {
  const user = useAuthStore((s) => s.user);

  if (!user) {
    return null;
  }

  if (!userHasPermission(user, permission)) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}
